<template>
	<div v-if="visible" ref="container" style="display:contents">
		<template v-if="state === 'ok'">
			<template v-for="(item, idx) in ld.items" :key="idx">
				<component :is="componentOfPart(item)" :part="partRef(item)" :data="data" :readonly="ro" :viewmode="vm"></component>
			</template>
		</template>
		<template v-else-if="state === 'loading' && !!ld.loading">
			<component :is="componentOfPart(ld.loading)" :part="partRef(ld.loading)" :data="data" :readonly="ro" :viewmode="vm"></component>
		</template>
		<template v-else-if="state === 'error' && !!ld.error">
			<component :is="componentOfPart(ld.error)" :part="partRef(ld.error)" :data="data" :readonly="ro" :viewmode="vm"></component>
		</template>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, onUnmounted} from "vue";
import useContainer, {type ContainerProps, containerProps} from "./Container";
import type {Loadable} from "@t/fd/layout/Loadable";
import {DataObject} from "@t/fd/FormPart.ts";
import {endpointUrl} from "@fd/containers/AddButton.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {EventBusListener} from "@fd/Types.ts";

const uprops = defineProps({
	...containerProps,
});
const props = uprops as ContainerProps;
const ld = computed<Loadable>(() => props.part as Loadable);
const {componentOfPart, partRef, localData, formData, visible, ro, vm} = useContainer(props);
const env = injectEnv();
const bus = connectEventBus();

const state = ref<"init"|"loading"|"ok"|"error">("init");
const data = ref<DataObject>({});
const canLoad = computed<boolean>(() => visible.value);
const loadCounter = ref<number>(1);
const container = ref<HTMLElement>();

let loadingTimer:number = 0;

const endpoint = computed<string>(() => {
	return endpointUrl(env, ld.value.endpoint, localData.value, formData, ld.value.params);
});

function containsFocus(): boolean {
	return !!container.value?.contains(document.activeElement);
}

async function loadData() {
	if (state.value !== "ok") state.value = "loading";
	const nr = await env.net.fetch(endpoint.value);
	if (nr.ok) {
		data.value = {
			...(await nr.json() as DataObject),
			/** Include access to the parent localData */
			get _parent() {
				return localData.value;
			}
		};
		state.value = "ok";
		if (typeof data.value._refresh === 'number') {
			// Do one-time reload
			setTimeout(() => {
				loadCounter.value++;
			}, data.value._refresh);
		}
	} else {
		state.value = "error";
	}
}

watch([canLoad, loadCounter],() => {
	if (canLoad.value && loadCounter.value > 0) {
		loadData();
	}
}, { immediate: true });

const refreshListener:EventBusListener = (...args:any[]) => {
	console.log("refreshListener called ", ...args);
	if (args.length === 0 || (ld.value.name === args[0])) {
		loadCounter.value++;
	}
};
bus.on("refresh:loadable", refreshListener);

onMounted(() => {
	if (ld.value.refreshSecs) {
		loadingTimer = setInterval(() => {
			if (!containsFocus()) loadCounter.value++;
			else console.log("Refresh postponed (a child is focussed)")
		}, ld.value.refreshSecs * 1000);
	}
});

onUnmounted(() => {
	if (loadingTimer) clearInterval(loadingTimer);
});
</script>

<style>

</style>